/**
 * Phase 6: AI Deep Learning
 * Targeted AI questions based on all previous context
 * (Merged from original "Learn" tab)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { 
  BrainCircuit, Loader2, Check, Play, SkipForward, 
  RefreshCw, Target, Lightbulb, CheckCircle2, HelpCircle
} from 'lucide-react';
import { 
  getLearningStatus, getNextQuestion, answerQuestion, 
  skipQuestion, generateLearningQuestions, resetPendingQuestions 
} from '../../services/api';

const Phase6AILearning = ({ onComplete, isAdmin }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const data = await getLearningStatus();
      const mapped = {
        total_learned: data.questions_answered || 0,
        pending: data.pending_questions || 0,
        skipped: data.skipped || 0,
        knowledge_percentage: data.knowledge_percentage || 0,
        total_questions_needed: data.total_questions_needed || 0,
        status: data.status
      };
      setStatus(mapped);
      const total = mapped.total_learned + mapped.pending + mapped.skipped;
      const progress = total > 0 ? Math.round((mapped.total_learned / Math.max(total, 10)) * 100) : 0;
      onCompleteRef.current(Math.min(progress, 100));
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const fetchNextQuestion = async () => {
    setQuestionLoading(true);
    try {
      const result = await getNextQuestion();
      setCurrentQuestion(result.question);
      setAnswerText('');
    } catch (error) {
      toast.error('Failed to fetch question');
    } finally {
      setQuestionLoading(false);
    }
  };

  const handleGenerateQuestions = async () => {
    setGenerating(true);
    try {
      const result = await generateLearningQuestions(3);
      toast.success(`Generated ${result.generated} new questions`);
      await fetchStatus();
      await fetchNextQuestion();
    } catch (error) {
      toast.error('Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleAnswer = async () => {
    if (!answerText.trim() || !currentQuestion) return;
    
    setSubmitting(true);
    try {
      await answerQuestion(currentQuestion.id, answerText);
      toast.success('Answer saved! Knowledge added.');
      setAnswerText('');
      await fetchStatus();
      await fetchNextQuestion();
    } catch (error) {
      toast.error('Failed to save answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!currentQuestion) return;
    
    try {
      await skipQuestion(currentQuestion.id);
      toast.info('Question skipped');
      await fetchNextQuestion();
    } catch (error) {
      toast.error('Failed to skip question');
    }
  };

  const handleStartLearning = async () => {
    if (status?.pending > 0) {
      // Has pending questions, just fetch next
      await fetchNextQuestion();
    } else {
      // No pending questions, generate first then fetch
      await handleGenerateQuestions();
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all pending questions? This will regenerate new questions.')) return;
    
    try {
      await resetPendingQuestions();
      toast.success('Questions reset');
      setCurrentQuestion(null);
      await fetchStatus();
    } catch (error) {
      toast.error('Failed to reset questions');
    }
  };

  if (statusLoading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <BrainCircuit className="w-5 h-5 text-indigo-600" />
          Phase 6: AI Deep Learning
        </CardTitle>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-gray-500">
          The AI asks targeted questions based on your business context, tables, and relationships. 
          Your answers help it understand nuances that can't be inferred from schema alone.
        </p>

        {/* Learning Stats */}
        {status && (
          <div className="grid grid-cols-4 gap-4">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{status.total_learned}</div>
              <div className="text-xs text-green-700">Learned</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-amber-600">{status.pending}</div>
              <div className="text-xs text-amber-700">Pending</div>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-600">{status.skipped}</div>
              <div className="text-xs text-gray-500">Skipped</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {Math.round((status.total_learned / Math.max(status.total_learned + status.pending, 1)) * 100)}%
              </div>
              <div className="text-xs text-blue-700">Complete</div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {status && (
          <div className="space-y-1">
            <Progress 
              value={(status.total_learned / Math.max(status.total_learned + status.pending, 1)) * 100} 
              className="h-2"
            />
          </div>
        )}

        {/* Current Question */}
        {questionLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
          </div>
        ) : currentQuestion ? (
          <div className="p-4 border rounded-lg space-y-4 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <HelpCircle className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-800">{currentQuestion.question}</h4>
                {currentQuestion.context && (
                  <p className="text-sm text-gray-500 mt-1">{currentQuestion.context}</p>
                )}
                {currentQuestion.category && (
                  <span className="inline-block mt-2 text-xs bg-white px-2 py-0.5 rounded border">
                    {currentQuestion.category}
                  </span>
                )}
              </div>
            </div>

            <Textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Type your answer here... Be specific and include examples if helpful."
              rows={4}
              className="bg-white"
            />

            <div className="flex gap-2">
              <Button 
                onClick={handleAnswer} 
                disabled={submitting || !answerText.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Submit Answer
              </Button>
              <Button variant="outline" onClick={handleSkip}>
                <SkipForward className="w-4 h-4 mr-2" />
                Skip
              </Button>
            </div>
          </div>
        ) : status?.pending === 0 ? (
          <div className="text-center p-8 bg-green-50 rounded-lg">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-green-700 font-medium">All questions answered!</p>
            <p className="text-sm text-green-600 mt-1">
              The AI has learned from your answers. Generate more questions to continue teaching it.
            </p>
            {isAdmin && (
              <Button 
                onClick={handleGenerateQuestions} 
                disabled={generating}
                className="mt-4"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lightbulb className="w-4 h-4 mr-2" />
                )}
                Generate More Questions
              </Button>
            )}
          </div>
        ) : (
          <div className="text-center p-6">
            <Target className="w-12 h-12 text-indigo-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">
              {status?.pending > 0 
                ? `${status.pending} questions waiting for your answers`
                : 'Ready to start learning?'
              }
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={handleStartLearning}>
                <Play className="w-4 h-4 mr-2" />
                {status?.pending > 0 ? 'Continue Learning' : 'Start Learning'}
              </Button>
              {isAdmin && status?.pending === 0 && (
                <Button variant="outline" onClick={handleGenerateQuestions} disabled={generating}>
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lightbulb className="w-4 h-4 mr-2" />
                  )}
                  Generate Questions
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="p-3 bg-blue-50 rounded-lg">
          <h5 className="text-sm font-medium text-blue-800 flex items-center gap-1">
            <Lightbulb className="w-4 h-4" /> Tips for Better Answers
          </h5>
          <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
            <li>Be specific - include table/column names when relevant</li>
            <li>Give examples - "A user can have multiple wallets, e.g., USD wallet, EUR wallet"</li>
            <li>Explain business rules - "Withdrawals require admin approval over $10,000"</li>
            <li>Mention edge cases - "Deleted users have status='deleted' but records are kept"</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default Phase6AILearning;
